import { Assets, IconButton } from '@nexds/web'
import { useTheme } from 'styled-components'

import { FuelData, FuelLayer } from '@/domain/protocols/Fuel'

import {
  Card,
  Details,
  DetailText,
  NotesRow,
  PhoneIcon,
  PhoneList,
  PhoneRow,
  PriceContainer,
  PriceLine,
  PriceMissing,
  PricePrefix,
  PriceUnit,
  PriceValue,
  ProviderName,
  TopRow,
  UpdatedAt
} from './FuelProviderCard.styles'

import { MetricsEvents } from '@/constants/metricsEvents'
import phoneIcon from '@/presentation/assets/fuel/phone.svg'
import { useI18n } from '@/presentation/hooks/useI18n'
import { FuelFormStateMutator } from '@/presentation/modules/MapScreen/states/FuelFormState'
import { sendMetrics } from '@/presentation/utils/sendMetrics'
import { formatNumberFixed } from '@/utils/number'

interface FuelProviderCardProps {
  adhpCode: string
  provider: FuelData
  activeLayer: FuelLayer
}

type ParsedPhone = {
  label: string
  whatsappLink: string | null
}

const formatDigitsAsPhone = (digits: string): string => {
  if (digits.length >= 12) {
    const countryCode = digits.slice(0, 2)
    const ddd = digits.slice(2, 4)
    const rest = digits.slice(4)
    return `+${countryCode} (${ddd}) ${rest}`
  }

  const ddd = digits.slice(0, 2)
  const rest = digits.slice(2)
  return `(${ddd}) ${rest}`
}

const parsePhone = (rawPhone: string): ParsedPhone => {
  const value = rawPhone.trim()
  const match = value.match(/^([\d+()\s-]*\d[\d+()\s-]*)(.*)$/)
  const phonePart = (match?.[1] ?? value).trim()
  const suffix = (match?.[2] ?? '').trim()
  const digits = phonePart.replace(/\D/g, '')

  const label = [formatDigitsAsPhone(digits), suffix].filter(Boolean).join(' ')

  if (digits.length < 10) return { label: value, whatsappLink: null }

  const normalizedPhone = digits.length <= 11 ? `55${digits}` : digits
  return { label, whatsappLink: `https://wa.me/${normalizedPhone}` }
}

function FuelProviderCard({ adhpCode, provider, activeLayer }: FuelProviderCardProps) {
  const theme = useTheme()
  const { t } = useI18n()

  const price = activeLayer === 'avgas' ? provider.avgasPrice : provider.jetPrice
  const noPriceMessage = activeLayer === 'avgas' ? t('FUEL_CARD_NO-PRICE_AVGAS') : t('FUEL_CARD_NO-PRICE_JET-A')
  const updatedAt = activeLayer === 'avgas' ? provider.lastAvgasUpdated : provider.lastJetUpdated
  const phones = (provider.phone ?? [])
    .map((item) => item?.trim())
    .filter((item): item is string => Boolean(item))
    .map(parsePhone)

  return (
    <Card>
      <TopRow>
        <ProviderName>{provider.provider}</ProviderName>
        <IconButton
          icon="Edit"
          color="ghost"
          radius="square"
          size="xs"
          onClick={() => {
            sendMetrics(MetricsEvents.FUEL_EDIT_PROVIDER_CLICKED, {
              adhpCode,
              fuelType: activeLayer,
              hasPrice: price != null
            })

            FuelFormStateMutator.showEditProvider(adhpCode, provider.provider, activeLayer)
          }}
        />
      </TopRow>

      <PriceContainer>
        <PriceLine>
          {price != null ? (
            <>
              <Assets.FuelPump color={theme.colors.neutralL5} size="md" />
              <PricePrefix>R$</PricePrefix>
              <PriceValue>{formatNumberFixed(price, 2)}</PriceValue>
              <PriceUnit>/litro</PriceUnit>
            </>
          ) : (
            <PriceMissing>{noPriceMessage}</PriceMissing>
          )}
        </PriceLine>
        {updatedAt && <UpdatedAt>{updatedAt}</UpdatedAt>}
      </PriceContainer>

      {(phones.length > 0 || provider.notes) && (
        <Details>
          {phones.length > 0 && (
            <PhoneList>
              {phones.map((phone, index) => {
                return (
                  <PhoneRow key={`${phone.label}-${index}`}>
                    <PhoneIcon src={phoneIcon} alt="telefone" aria-hidden="true" />
                    <DetailText>{phone.label}</DetailText>
                    {phone.whatsappLink && (
                      <IconButton
                        icon="Whatsapp"
                        color="ghost"
                        radius="square"
                        size="xs"
                        iconColor={theme.colors.neutralL5}
                        aria-label={`Abrir WhatsApp para ${phone.label}`}
                        onClick={() => {
                          if (!phone.whatsappLink) return

                          // Sem número de telefone no payload — é dado de contato.
                          sendMetrics(MetricsEvents.FUEL_PROVIDER_WHATSAPP_CLICKED, {
                            adhpCode,
                            fuelType: activeLayer,
                            hasPrice: price != null
                          })

                          window.open(phone.whatsappLink, '_blank', 'noopener,noreferrer')
                        }}
                      />
                    )}
                  </PhoneRow>
                )
              })}
            </PhoneList>
          )}
          {provider.notes && (
            <NotesRow>
              <Assets.Info color={theme.colors.neutralL5} size="sm" />
              <DetailText>{provider.notes}</DetailText>
            </NotesRow>
          )}
        </Details>
      )}
    </Card>
  )
}

export { FuelProviderCard }
